import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

export default function AdminCalculator() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/calculator/config").then(({ data }) => setCfg(normalize(data)));
  }, []);

  if (!cfg) return <div className="bg-white rounded-2xl border border-slate-200 p-8">Lade…</div>;

  // ----- Services
  const setServiceField = (i, k, v) => {
    const services = [...cfg.services]; services[i] = { ...services[i], [k]: v };
    setCfg({ ...cfg, services });
  };
  const addService = () => setCfg({ ...cfg, services: [...cfg.services, {
    key: `service_${Date.now()}`, label: "Neue Leistung", base: 1000, icon: "Briefcase",
    pricing_type: "flat", default_executive_h: 0, default_clerk_h: 0, enabled: true,
  }] });
  const removeService = (i) => setCfg({ ...cfg, services: cfg.services.filter((_, idx) => idx !== i) });

  // ----- Addons
  const setAddonField = (i, k, v) => {
    const addons = [...cfg.addons]; addons[i] = { ...addons[i], [k]: v };
    setCfg({ ...cfg, addons });
  };
  const addAddon = () => setCfg({ ...cfg, addons: [...cfg.addons, { key: `addon_${Date.now()}`, label: "Neuer Zusatz", price: 100, enabled: true }] });
  const removeAddon = (i) => setCfg({ ...cfg, addons: cfg.addons.filter((_, idx) => idx !== i) });

  // ----- Complexities (LIST, not dict)
  const setComplexityField = (i, k, v) => {
    const list = [...cfg.complexities];
    list[i] = { ...list[i], [k]: k === "multiplier" ? Number(v) : v };
    setCfg({ ...cfg, complexities: list });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...cfg,
        services: cfg.services.map((s) => ({ ...s, base: Number(s.base) || 0,
          default_executive_h: Number(s.default_executive_h) || 0,
          default_clerk_h: Number(s.default_clerk_h) || 0 })),
        addons: cfg.addons.map((a) => ({ ...a, price: Number(a.price) || 0 })),
        complexities: cfg.complexities.map((c) => ({ ...c, multiplier: Number(c.multiplier) || 1 })),
        employee_fee: Number(cfg.employee_fee) || 0,
        vat_rate: Number(cfg.vat_rate) || 0,
        hourly_executive_chf: Number(cfg.hourly_executive_chf) || 0,
        hourly_clerk_chf: Number(cfg.hourly_clerk_chf) || 0,
      };
      await api.put("/admin/calculator/config", payload);
      toast.success("Konfiguration gespeichert");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="admin-calculator">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1A2433] mb-1">Kalkulator-Konfiguration</h1>
          <p className="text-slate-600 text-sm">Leistungen, Preise und Faktoren live anpassen.</p>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="calc-cfg-save">
          <Save className="w-4 h-4" /> {busy ? "Speichere…" : "Speichern"}
        </button>
      </div>

      {/* Leistungen */}
      <Section title="Leistungen" onAdd={addService}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-2 py-2">Key</th>
                <th className="px-2 py-2">Bezeichnung</th>
                <th className="px-2 py-2 w-28">Basispreis</th>
                <th className="px-2 py-2 w-28">Abrechnung</th>
                <th className="px-2 py-2 w-20">GF Std</th>
                <th className="px-2 py-2 w-20">MA Std</th>
                <th className="px-2 py-2 w-16">Aktiv</th>
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {cfg.services.map((s, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-2"><Inp value={s.key} onChange={(v) => setServiceField(i, "key", v)} mono /></td>
                  <td className="px-2 py-2"><Inp value={s.label} onChange={(v) => setServiceField(i, "label", v)} /></td>
                  <td className="px-2 py-2"><Inp type="number" value={s.base} onChange={(v) => setServiceField(i, "base", v)} /></td>
                  <td className="px-2 py-2">
                    <select value={s.pricing_type || "flat"} onChange={(e) => setServiceField(i, "pricing_type", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:border-[#991B1B] text-sm">
                      <option value="flat">Pauschal</option>
                      <option value="hourly">Stunden</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </td>
                  <td className="px-2 py-2"><Inp type="number" value={s.default_executive_h ?? 0} onChange={(v) => setServiceField(i, "default_executive_h", v)} /></td>
                  <td className="px-2 py-2"><Inp type="number" value={s.default_clerk_h ?? 0} onChange={(v) => setServiceField(i, "default_clerk_h", v)} /></td>
                  <td className="px-2 py-2 text-center">
                    <input type="checkbox" checked={!!s.enabled} onChange={(e) => setServiceField(i, "enabled", e.target.checked)} className="w-4 h-4 accent-[#991B1B]" />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => removeService(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Zusatzleistungen */}
      <Section title="Zusatzleistungen" onAdd={addAddon}>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-2 py-2">Key</th>
              <th className="px-2 py-2">Bezeichnung</th>
              <th className="px-2 py-2 w-32">Preis</th>
              <th className="px-2 py-2 w-20">Aktiv</th>
              <th className="px-2 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {cfg.addons.map((a, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-2 py-2"><Inp value={a.key} onChange={(v) => setAddonField(i, "key", v)} mono /></td>
                <td className="px-2 py-2"><Inp value={a.label} onChange={(v) => setAddonField(i, "label", v)} /></td>
                <td className="px-2 py-2"><Inp type="number" value={a.price} onChange={(v) => setAddonField(i, "price", v)} /></td>
                <td className="px-2 py-2 text-center">
                  <input type="checkbox" checked={!!a.enabled} onChange={(e) => setAddonField(i, "enabled", e.target.checked)} className="w-4 h-4 accent-[#991B1B]" />
                </td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => removeAddon(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Komplexitäten */}
      <Section title="Komplexitäts-Stufen">
        <div className="grid md:grid-cols-3 gap-4">
          {cfg.complexities.map((c, i) => (
            <div key={c.key || i} className="bg-slate-50 rounded-xl p-4 space-y-2.5 border border-slate-200">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Key (fix)</label>
                <input value={c.key || ""} disabled className="w-full px-2 py-1.5 rounded-md border border-slate-200 text-sm font-mono text-xs bg-slate-100" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Anzeige-Label</label>
                <Inp value={c.label ?? ""} onChange={(v) => setComplexityField(i, "label", v)} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Multiplikator</label>
                <Inp type="number" step="0.05" value={c.multiplier} onChange={(v) => setComplexityField(i, "multiplier", v)} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Beschreibung</label>
                <textarea value={c.description || ""} onChange={(e) => setComplexityField(i, "description", e.target.value)}
                  rows={4}
                  className="w-full px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:border-[#991B1B] text-sm" />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Globale Werte */}
      <Section title="Globale Werte">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <NumField label="Stundensatz Geschäftsführung (CHF)" value={cfg.hourly_executive_chf}
                    onChange={(v) => setCfg({ ...cfg, hourly_executive_chf: v })} />
          <NumField label="Stundensatz Kaufm. MA (CHF)" value={cfg.hourly_clerk_chf}
                    onChange={(v) => setCfg({ ...cfg, hourly_clerk_chf: v })} />
          <NumField label="Pauschale pro Mitarbeiter (CHF)" value={cfg.employee_fee}
                    onChange={(v) => setCfg({ ...cfg, employee_fee: v })} />
          <NumField label="MWST-Satz (z. B. 0.081)" step="0.001" value={cfg.vat_rate}
                    onChange={(v) => setCfg({ ...cfg, vat_rate: v })} />
        </div>
      </Section>
    </div>
  );
}

// Convert legacy dict-style complexities → list-style on the fly so older
// configs in DB don't crash the UI.
function normalize(cfg) {
  if (!cfg) return cfg;
  if (cfg.complexities && !Array.isArray(cfg.complexities) && typeof cfg.complexities === "object") {
    const labels = { einfach: "Einfach", standard: "Standard", komplex: "Speziell" };
    cfg.complexities = Object.entries(cfg.complexities).map(([key, multiplier]) => ({
      key, label: labels[key] || key, multiplier: Number(multiplier), description: "",
    }));
  }
  cfg.hourly_executive_chf = cfg.hourly_executive_chf ?? 150;
  cfg.hourly_clerk_chf = cfg.hourly_clerk_chf ?? 70;
  return cfg;
}

function Section({ title, onAdd, children }) {
  return (
    <div className="mt-8 border-t border-slate-200 pt-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-bold text-[#1A2433]">{title}</h2>
        {onAdd && (
          <button onClick={onAdd} className="text-sm text-[#991B1B] font-medium hover:underline flex items-center gap-1">
            <Plus className="w-4 h-4" /> Hinzufügen
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Inp({ value, onChange, type = "text", mono, step }) {
  return (
    <input
      type={type}
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:border-[#991B1B] text-sm ${mono ? "font-mono text-xs" : ""}`}
    />
  );
}

function NumField({ label, value, onChange, step = "1" }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}
             className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]" />
    </div>
  );
}
